<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="invoices"
      :loading="loading"
      class="invoice-table py-2 px-4"
      hide-default-footer
      disable-pagination
      item-key="id"
      :expanded.sync="expanded"
      show-expand
      :single-expand="false"
    >
      <!-- Month column -->
      <template v-slot:item.month="{ item }">
        <span class="font-weight-medium subtitle-1">{{ formatMonth(item.month) }}</span>
      </template>

      <!-- Partner column (broker aggregate view) -->
      <template v-slot:item.partner_name="{ item }">
        <div class="d-flex flex-column">
          <span class="font-weight-medium caption" style="max-width: 200px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;" :title="item.partner_name">{{ item.partner_name }}</span>
          <span class="caption grey--text text--lighten-1" v-if="item.partner_cnpj">{{ convertToCNPJ(item.partner_cnpj) }}</span>
        </div>
      </template>

      <!-- Amount column -->
      <template v-slot:item.amount="{ item }">
        <span class="font-weight-bold text-h6 font-family-monospace">R$ {{ formatAmount(item.amount) }}</span>
      </template>

      <!-- Status column -->
      <template v-slot:item.status="{ item }">
        <v-chip
          small
          :color="getStatusColor(item.status)"
          text-color="white"
          class="font-weight-bold px-4"
          style="min-width: 90px; text-align: center; justify-content: center;"
        >
          {{ getStatusText(item.status) }}
        </v-chip>
      </template>

      <!-- Actions column (broker only) -->
      <template v-slot:item.actions="{ item }">
        <v-btn
          v-if="item.status !== 'InProgress'"
          x-small
          outlined
          rounded
          :color="item.paid === 1 ? 'warning' : 'success'"
          class="action-toggle-btn font-weight-bold px-3 py-1"
          @click="$emit('toggle-paid', item)"
        >
          <v-icon left x-small>{{ item.paid === 1 ? 'mdi-close-circle-outline' : 'mdi-check-circle-outline' }}</v-icon>
          {{ item.paid === 1 ? $t('billing.actions.markUnpaid') : $t('billing.actions.markPaid') }}
        </v-btn>
      </template>

      <!-- Expanded breakdown row -->
      <template v-slot:expanded-item="{ headers: expandedHeaders, item }">
        <invoice-expanded-row :item="item" :headers="expandedHeaders" />
      </template>

      <template v-slot:no-data>
        <div class="py-8 text-center grey--text subtitle-1">
          <v-icon large color="grey lighten-1" class="mb-2">mdi-invoice-text-outline</v-icon>
          <div>{{ $t('billing.table.noInvoices') }}</div>
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import convertToCNPJ, { formatDateYM, formatBRLPretty } from "@/utils";
import InvoiceExpandedRow from "./InvoiceExpandedRow.vue";

export default {
  components: {
    InvoiceExpandedRow,
  },
  props: {
    invoices: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    showActions: {
      type: Boolean,
      default: false,
    },
    showPartnerColumn: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      expanded: [],
    };
  },
  computed: {
    headers() {
      const cols = [
        { text: "", value: "data-table-expand" },
        { text: this.$t('billing.table.colMonth'), value: "month", align: 'start', sortable: false },
      ];
      if (this.showPartnerColumn) {
        cols.push({ text: this.$t('billing.table.colPartner'), value: "partner_name", align: 'start', sortable: false });
      }
      cols.push(
        { text: this.$t('billing.table.colAmount'), value: "amount", align: 'end', sortable: false },
        { text: this.$t('billing.table.colStatus'), value: "status", align: 'center', sortable: false },
      );
      if (this.showActions) {
        cols.push({ text: this.$t('billing.table.colActions'), value: "actions", align: 'center', sortable: false });
      }
      return cols;
    },
  },
  methods: {
    convertToCNPJ,
    formatMonth(monthStr) {
      if (!monthStr) return "";
      const [year, month] = monthStr.split("-");
      const date = new Date(year, parseInt(month) - 1, 1);
      return formatDateYM(date.getTime() / 1000);
    },
    formatAmount(amount) {
      const n = Number(amount);
      if (amount === undefined || amount === null || !Number.isFinite(n)) return "0,00";
      return formatBRLPretty(n);
    },
    getStatusColor(status) {
      if (status === "Paid") return "success";
      if (status === "Due") return "warning";
      if (status === "InProgress") return "info";
      return "error";
    },
    getStatusText(status) {
      if (status === "Paid") return this.$t('billing.status.paid');
      if (status === "Due") return this.$t('billing.status.due');
      if (status === "InProgress") return this.$t('billing.status.inProgress');
      return this.$t('billing.status.overdue');
    }
  },
};
</script>

<style scoped>
.invoice-table {
  background: transparent !important;
  border: none !important;
}

.action-toggle-btn {
  letter-spacing: 0.5px;
  font-family: inherit;
  transition: all 0.2s ease-in-out;
}

.action-toggle-btn:hover {
  transform: scale(1.05);
}
</style>
